import {
  BehaviorSubject,
  combineLatest,
  Observable,
  race,
  Subject
} from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  OnDestroy,
  ViewChild
} from '@angular/core';
import {
  filter,
  map,
  switchMap,
  tap,
  take,
  shareReplay,
  first,
  takeUntil,
  combineAll
} from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import {
  selectPostView,
  selectPosts,
  selectComments,
  selectStatus,
  selectCommentsStatus,
  selectCommentsCount,
  selectReplies
} from '../../../shared/ui/post/data/store/selector';
import {
  loadPostsById,
  loadPostsByGroupId,
  addPost,
  paginatePosts,
  paginateGroupPosts,
  addGroupPost,
  deletePost,
  likePost,
  complainePost,
  sharePost,
  updatePost,
  loadCommentsForPost,
  addCommentToPost,
  deleteComment,
  likeComment,
  replyComment,
  clearComments
} from '../../../shared/ui/post/data/store/actions';
import { Meta, Title } from '@angular/platform-browser';

import { PostsService } from '../../../shared/ui/post/data/store/service';
import { AuthService } from '../../../core/services/auth.service';
import { PostState } from '../../../shared/ui/post/data/model/state';
import { Actions } from '@ngrx/effects';
import {
  IMessage,
  IMessageCtx
} from '../../../shared/components/message/message.component';
import { IPostsCtx } from '../../../shared/components/posts/list/posts.component';
import { IPost } from '../../../shared/models/post';
import { ICommentForm } from '../../../shared/models/comment';

import { Group } from 'src/app/core/services/group/group.model';
import {
  loadGroupRequests,
  resetGroupRequests,
  unrequestGroup,
  accessGroup,
  resetGroupMembers,
  loadGroupMembers,
  unsubscribeGroup,
  loadGroupInfo,
  subscribeGroup,
  clearGroup,
  updateGroupInfo,
  removeGroup
} from 'src/app/core/services/group/group.actions';
import {
  State as GroupState,
  selectGroupRequests,
  selectGroupMembers,
  selectGroupInfo
} from 'src/app/core/services/group/group.reducer';
import { User, Request } from 'src/app/core/services/group/group.model';

import {
  selectArticles,
  State as EduniverseState
} from 'src/app/core/services/eduniverse/eduniverse.reducer';
import { loadArticles } from 'src/app/core/services/eduniverse/eduniverse.actions';
import { Article } from 'src/app/core/services/eduniverse/eduniverse.model';

import {
  addContact,
  removeContact
} from 'src/app/core/services/contacts/contacts.actions';
import { ModalsService } from '../../../shared/modals/modals.service';
import { BaseService } from '../../../core/services/base.service';
import { SavedPostsFacade } from '../../../shared/stores/posts/saved/posts.saved.facade';

import { CreatePrivateGroupDialogComponent } from 'src/app/shared/components/create-private-group-dialog/create-private-group-dialog.component';
import { CreateSchoolClubChapterDialogComponent } from 'src/app/shared/components/create-school-club-chapter-dialog/create-school-club-chapter-dialog.component';
import { RoutingService } from 'src/app/core/services/routing.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { PostsFacade } from 'src/app/modules/posts/store';
import { GroupsFacade } from 'src/app/core/services/groups/groups.facade';
import { LeftNavMenuService } from 'src/app/templates/services';

@Component({
  selector: 'app-group-view',
  templateUrl: './group-view.component.html',
  styleUrls: ['./group-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupViewComponent implements OnInit, OnDestroy {
  requests$: Observable<{ requests: Request[]; loadRequests: boolean }>;
  members$: Observable<{
    members: User[];
    loadMembers: boolean;
    count: number;
    nextPageToLoad: number;
  }>;
  articles$: Observable<{ totalCount: number; items: Article[] }>;

  @ViewChild(ConfirmDialogComponent, { static: true })
  confirmDialog: ConfirmDialogComponent;

  clubInfo$ = new BehaviorSubject(null);

  readonly groupInfo$ = this.groupStore.select(selectGroupInfo()).pipe(
    filter((group) => !!group),
    tap((group) => this.onUpdate(group)),
    filter((group) => !this.isGroupRemoved(group)),
    filter((group) => {
      if (this.isClub(group)) {
        this.clubInfo$.next(group);
        return false;
      }

      return true;
    })
  );
  groupPosts$: Observable<any>;
  groupNetwork$: Observable<any>;
  groupId$: Observable<number>;
  unsub$: Subject<any> = new Subject<any>();
  readonly status$ = this.postStore.pipe(select(selectStatus()));
  private group_id: number;

  readonly messageCtx: IMessageCtx = {
    placeholder: `Post an update, article or question`,
    submit: (message) => this.submit(message),
    isSupportTags: true,
    isSupportLinkPreview: true
  };

  constructor(
    private store: Store,
    private postStore: Store<PostState>,
    private actions: Actions,
    private route: ActivatedRoute,
    private readonly _router: Router,
    private titleService: Title,
    private metaTagService: Meta,
    private posts: PostsService,
    private groupStore: Store<GroupState>,
    private eduniverseStore: Store<EduniverseState>,
    private readonly authService: AuthService,
    readonly modal: ModalsService,
    readonly base: BaseService,
    readonly savedPosts: SavedPostsFacade,
    private dialog: MatDialog,
    private location: Location,
    private rountingService: RoutingService,
    private readonly _leftNavMenuService: LeftNavMenuService,
    private readonly groupsFacade: GroupsFacade,
    private readonly postFacade: PostsFacade
  ) {
    this._leftNavMenuService.hideLeftNavMenu();

    this._router.events
      .pipe(
        filter((_) => _ instanceof NavigationEnd),
        tap((_) => this._leftNavMenuService.hideLeftNavMenu()),
        takeUntil(this.unsub$)
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.requests$ = this.groupStore.pipe(select(selectGroupRequests()));
    this.members$ = this.groupStore.pipe(select(selectGroupMembers()));
    this.articles$ = this.eduniverseStore.pipe(select(selectArticles()));

    this.groupId$ = this.route.params.pipe(
      filter(({ id }) => id),
      map(({ id }) => Number(id)),
      tap((id) => (this.group_id = id))
    );

    this.groupId$
      .pipe(
        tap((id) => this.groupStore.dispatch(clearGroup())),
        tap((id) => this.groupStore.dispatch(loadGroupInfo({ id })))
      )
      .subscribe((_) => _);

    this.groupInfo$.pipe(takeUntil(this.unsub$)).subscribe(this.onUpdate);
    this.groupInfo$.pipe(takeUntil(this.unsub$)).subscribe(this.selectArticles);
    this.groupInfo$
      .pipe(takeUntil(this.unsub$))
      .subscribe(this.selectGroupRequests);
    this.groupInfo$
      .pipe(takeUntil(this.unsub$))
      .subscribe(this.selectGroupMembers);
    this.groupInfo$
      .pipe(
        takeUntil(this.unsub$),
        map((info) => info.id)
      )
      .subscribe((id) => (this.group_id = id));

    this.groupPosts$ = this.groupInfo$.pipe(
      tap(({ id }) => this.postStore.dispatch(loadPostsByGroupId({ id }))),
      switchMap(() => this.postStore.pipe(select(selectPosts({}))))
    );
  }

  ngOnDestroy() {
    this.store.dispatch(clearComments());
    this.store.dispatch(clearGroup());
    this.unsub$.next('');
    this.unsub$.complete();
  }

  get isCanSendMessage$() {
    return this.groupInfo$.pipe(map((_) => _?.access_posted));
  }

  get isAuthor$() {
    return this.groupInfo$.pipe(
      filter((_) => !!_),
      map((group) => group.is_autor)
    );
  }

  get isAuthorized$() {
    return this.authService.token$.pipe(map((_) => !!_));
  }

  get isCurrentUserAdmin$() {
    return this.authService.user$.pipe(map((_) => _?.is_admin));
  }

  get isAdminView$() {
    return combineLatest([this.groupInfo$, this.isCurrentUserAdmin$]).pipe(
      // NOTE:
      // make moderation logic available only for public and inhouse discussions
      map((_) => _[0].access !== 3 && !this.isClub(_[0]) && _[1])
    );
  }

  isClub(group: any) {
    return !!group?.body?.id || !!group?.org?.id;
  }

  isMe(user) {
    return user.id === this.authService.userID();
  }

  onAccept(user) {
    this.groupId$
      .pipe(
        filter((id) => !!id),
        map((id: number) =>
          this.postStore.dispatch(accessGroup({ group_id: id, user }))
        )
      )
      .subscribe((_) => _);
  }

  onReject(user) {
    this.groupId$
      .pipe(
        filter((id) => !!id),
        map((id: number) =>
          this.postStore.dispatch(
            unrequestGroup({ group_id: id, user_id: user.id })
          )
        )
      )
      .subscribe((_) => _);
  }

  onProfileConnect(id: number) {
    this.store.dispatch(addContact({ id }));
  }

  onProfileDisconnect(id: number) {
    this.store.dispatch(removeContact({ id }));
  }

  onEditGroup = (groupInfo) => {
    if (groupInfo.access === 3) {
      return this.editPrivateGroup(groupInfo);
    }

    return this.editSchoolClubOrChaterGroup(groupInfo);
  };

  onRemove = (groupInfo?: Group) => {
    if (!groupInfo) {
      return;
    }

    this.confirmDialog
      .confirm(`Are you sure you want to delete ${groupInfo.name} ?`)
      .pipe(
        filter((_) => !!_),
        map((_) => this.groupStore.dispatch(removeGroup({ id: groupInfo.id })))
      )
      .subscribe((_) => _);
  };

  onGroupViewMemberUpdateToModerator(userId: number) {
    this.groupId$
      .pipe(
        take(1),
        tap((_) => this.groupsFacade.updateUserToModerator(_, userId))
      )
      .subscribe((_) => _);
  }

  onGroupViewMemberUpdateToMember(userId: number) {
    this.groupId$
      .pipe(
        take(1),
        tap((_) => this.groupsFacade.updateUserToMember(_, userId))
      )
      .subscribe((_) => _);
  }

  onLoadMoreMembers() {
    combineLatest([this.groupId$, this.members$])
      .pipe(
        take(1),
        map((_) => ({
          id: _[0],
          nextPageToLoad: _[1].nextPageToLoad
        })),
        filter((_) => !!_.id),
        tap((_) =>
          this.groupStore.dispatch(
            loadGroupMembers({
              id: _.id,
              page: _.nextPageToLoad,
              searchTerm: ''
            })
          )
        )
      )
      .subscribe((_) => _);
  }

  private editSchoolClubOrChaterGroup = (groupInfo) => {
    console.log('groupInfo: ', groupInfo);
    const dialogRef = this.dialog.open(CreateSchoolClubChapterDialogComponent, {
      data: { groupInfo }
    });

    dialogRef
      .afterClosed()
      .pipe(
        tap((_) => console.log('Group: ', _)),
        filter((res) => res && res.type === 'submit'),
        tap((res) =>
          this.groupStore.dispatch(
            updateGroupInfo({ id: res.id, group: res.payload })
          )
        )
      )
      .subscribe((_) => _);
  };

  private editPrivateGroup = (groupInfo) => {
    const dialogRef = this.dialog.open(CreatePrivateGroupDialogComponent, {
      data: { groupInfo }
    });

    dialogRef
      .afterClosed()
      .pipe(
        tap((_) => console.log('private Group: ', _)),
        filter((res) => res && res.type === 'submit'),
        map((res) =>
          this.groupStore.dispatch(
            updateGroupInfo({ id: res.id, group: res.payload })
          )
        )
      )
      .subscribe((_) => _);
  };

  private onUpdate = (group: Group) => {
    if (group.removed) {
      console.log('group has been removed: ', group);
      return this.rountingService.goToGroups();
    }

    // Why do we have this ?
    // this.location.replaceState(this.rountingService.getGroupLink(group.id, group.name).join('/'));
  };

  private selectGroupRequests = (group: any) => {
    if (!group) {
      return;
    }

    this.postStore.dispatch(resetGroupRequests());

    if (!group.is_autor) {
      return;
    }

    this.groupStore.dispatch(
      loadGroupRequests({ id: group.id, page: 1, searchTerm: '' })
    );
  };

  private selectGroupMembers = (group: any) => {
    this.isAuthorized$
      .pipe(
        filter((_) => _),
        take(1),
        tap((_) => {
          this.postStore.dispatch(resetGroupMembers());
          this.groupStore.dispatch(
            loadGroupMembers({ id: group.id, page: 1, searchTerm: '' })
          );
        }),
        takeUntil(this.unsub$)
      )
      .subscribe((_) => _);
  };

  private selectArticles = () => {
    this.isAuthorized$
      .pipe(
        filter((_) => _),
        take(1),
        tap((_) =>
          this.postStore.dispatch(
            loadArticles({ category: undefined, page: 1, term: '' })
          )
        ),
        takeUntil(this.unsub$)
      )
      .subscribe((_) => _);
  };

  private submit(message: IMessage) {
    this.groupId$
      .pipe(
        filter((_) => !!_),
        take(1),
        tap((_) => this.postFacade.addGroupPost(_, message)),
        takeUntil(this.unsub$)
      )
      .subscribe((_) => _);
  }

  joinEventsGroup(groupInfo) {
    const { id, is_subscriber, is_requested } = groupInfo;

    if (!is_subscriber && !is_requested) {
      this.groupStore.dispatch(
        subscribeGroup({ group_id: id, user_id: this.authService.userID() })
      );
    } else if (is_subscriber) {
      this.groupStore.dispatch(
        unsubscribeGroup({ group_id: id, user_id: this.authService.userID() })
      );
    } else if (is_requested) {
      this.groupStore.dispatch(
        unrequestGroup({ group_id: id, user_id: this.authService.userID() })
      );
    }
  }

  readonly postsCtx: IPostsCtx = {
    status: this.status$,
    delete: ({ id }) => this.postStore.dispatch(deletePost({ id })),
    like: ({ id }) => this.postStore.dispatch(likePost({ id })),
    complain: ({ id }) => this.postStore.dispatch(complainePost({ id })),
    share: ({ id }, message) => {
      this.postFacade.sharePost(id, message);
    },
    update: async (post, message) => {
      this.postFacade.updatePost(post.id, message);
    },
    save: (post) => {
      this.savedPosts.toggleSave(post);
    },
    paginate: (_, page) => {
      this.postStore.dispatch(
        paginateGroupPosts({ id: this.group_id, nextPage: page })
      );
    },
    modifyers: {
      isGroup: true,
      isForbidComments: this.isCanSendMessage$.pipe(map((is) => !is))
    },
    comments: {
      add: async (post, message) => {
        const form = await this.getCommentForm(post, message);
        this.postStore.dispatch(addCommentToPost({ comment: form }));
      },
      delete: (post, comment) => {
        this.store.dispatch(deleteComment({ comment }));
      },
      like: (post, comment) => this.store.dispatch(likeComment({ comment })),
      reply: async (post, comment, message) => {
        const form = await this.getCommentForm(post, message);
        form['comment_id'] = comment.id;
        this.postStore.dispatch(replyComment({ comment: form }));
      },
      get: (post) => {
        return {
          values: this.store.pipe(select(selectComments(post.id))),
          status: this.store.pipe(select(selectCommentsStatus(post.id))),
          count: this.store.pipe(select(selectCommentsCount(post.id)))
        };
      },
      paginate: (post, comments) => {
        this.store.dispatch(
          loadCommentsForPost({ post, offset: comments.length })
        );
      },
      replies: {
        get: (_, comment) => {
          return this.store.select(selectReplies(comment.id));
        }
      }
    }
  };

  protected async getCommentForm(
    post: IPost,
    message: IMessage
  ): Promise<ICommentForm> {
    return {
      post_id: post.id,
      text: message.text,
      foto: message.images,
      file: message.docs,
      links: message.linksToPreview
      // comment_id: comment.id,
    };
  }

  public invite() {
    const placeholder = `student@email.com, teacher@email.com`;

    this.modal
      .openInvite(placeholder)
      .afterClosed()
      .pipe(takeUntil(this.unsub$))
      .subscribe((email: string) => {
        if (!email) return; // #Return#
        this.base
          .post(`group/invite/${this.group_id}`, { email })
          .subscribe((resp) => {});
      });
  }

  private isGroupRemoved(group: Group) {
    if ('removed' in group) {
      return group.removed;
    }

    return false;
  }
}
